import type { RailLayout } from "@/data/rail-branches";
import type { BookTimelineLayout } from "../../../book/BookLayout";
import type { ActiveLocationGroup } from "@/lib/timeline/location";
import { onStageRunIds } from "@/lib/timeline/location";
import { locationBranchKey, locationRunRole } from "@/lib/timeline/location/LocationRun";
import { locationRunVisibleAtPlayhead } from "@/lib/timeline/location/LocationMarker";
import { allLocationRuns } from "@/lib/timeline/location/LocationRun";
import { partRunPhase } from "../../../rail/PartRunPhase/phase";
import { openRailLanes } from "../../../rail/RailLanes/lanes";
import { characterGroupsAtPlayhead } from "../../../character/ActiveCharacterGroup/atPlayhead";

export function activeTrunkBranchIds(
  rail: RailLayout,
  playheadMs: number,
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const branch of rail.branches) {
    if (
      playheadMs >= branch.startMs
      && playheadMs < branch.endMs
      && branch.lane === 0
    ) {
      ids.add(branch.id);
    }
  }
  return ids;
}

export function trunkOnStageStacks(
  layout: BookTimelineLayout,
  playheadMs: number,
  branchId: string,
): { primaryRunId?: string; onStageSecondaryRunIds: string[] } {
  let primaryRunId: string | undefined;
  let primaryEndMs = -1;
  const onStageSecondaryRunIds: string[] = [];
  for (const run of allLocationRuns(layout)) {
    if (locationBranchKey(run, layout) !== branchId) {
      continue;
    }
    if (!locationRunVisibleAtPlayhead(layout, run, playheadMs)) {
      continue;
    }
    if (locationRunRole(run) === "primary") {
      if (run.startMs > playheadMs) {
        continue;
      }
      if (run.endMs > primaryEndMs) {
        primaryRunId = run.runId;
        primaryEndMs = run.endMs;
      }
      continue;
    }
    if (partRunPhase(layout.rail, run, playheadMs).kind === "active") {
      onStageSecondaryRunIds.push(run.runId);
    }
  }
  onStageSecondaryRunIds.sort((a, b) => a.localeCompare(b));
  return { primaryRunId, onStageSecondaryRunIds };
}

export function trunkVisiblePartRunIds(
  layout: BookTimelineLayout,
  playheadMs: number,
  branchId: string,
): string[] {
  const runIds: string[] = [];
  for (const run of allLocationRuns(layout)) {
    if (locationBranchKey(run, layout) !== branchId) {
      continue;
    }
    if (!locationRunVisibleAtPlayhead(layout, run, playheadMs)) {
      continue;
    }
    if (locationRunRole(run) !== "secondary") {
      continue;
    }
    runIds.push(run.runId);
  }
  runIds.sort((a, b) => a.localeCompare(b));
  return runIds;
}

export function trunkOnStagePlaceLabels(
  layout: BookTimelineLayout,
  primaryRunId: string | undefined,
  onStageSecondaryRunIds: readonly string[],
): string[] {
  const labelByRunId = new Map(
    allLocationRuns(layout).map((run) => [run.runId, run.locationLabel]),
  );
  const lines: string[] = [];
  if (primaryRunId !== undefined) {
    lines.push(labelByRunId.get(primaryRunId) ?? primaryRunId);
  }
  for (const runId of onStageSecondaryRunIds) {
    lines.push(labelByRunId.get(runId) ?? runId);
  }
  return lines;
}

export function trunkCharacterIdsOnBranch(
  rail: RailLayout,
  playheadMs: number,
  branchId: string,
): string[] {
  const ids = new Set<string>();
  for (const group of characterGroupsAtPlayhead(rail, playheadMs)) {
    if (group.branchId !== branchId) {
      continue;
    }
    for (const characterId of group.markerIds) {
      ids.add(characterId);
    }
  }
  return [...ids].sort((a, b) => a.localeCompare(b));
}

export function trunkBranchesNeedingOnStageDebug(
  layout: BookTimelineLayout,
  playheadMs: number,
  groups: readonly ActiveLocationGroup[],
): string[] {
  if (openRailLanes(layout.rail.branches, playheadMs) <= 1) {
    return [];
  }
  const handled = new Set(
    groups
      .filter((group) => onStageRunIds(group, layout, playheadMs).length > 0)
      .map((group) => group.branchId),
  );
  return [...activeTrunkBranchIds(layout.rail, playheadMs)].filter(
    (branchId) => !handled.has(branchId),
  );
}
